/**
 * Transfer editor container
 */
import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { select } from '@angular-redux/store';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  every,
  find,
  get,
  gt,
  has,
  head,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  lowerCase,
  lt,
  merge,
  negate,
  some,
  sortBy,
  split,
  startsWith,
  toLower,
  upperFirst,
} from 'lodash';

import { observableCombineLatest, observableOf, rx } from '@shared/rx';
import {
  TRANSFER_STATUS_NAME_OPTIONS,
  TRANSFER_STATUS_VALUE,
} from '@shared/types/enums/transfer-status.enum';
import {
  COMMON_ACTION_NAMES,
  ENTITY_TYPES,
  EntityValidateResponse,
  IBaseEntityType,
  IFormRules,
  IGridFlatInventoryItemVenue,
  IModalDialogOptions,
  IPageJumpRouteState,
  ITransfer,
  STATUS_ACTION_MAPPING,
  COMMON_ACTION_TYPES,
  TRANSFER_SAVE_OPERATION,
  COMMON_ACTION_TYPES_REJECT,
  CustomRedirect,
  ACTION_NAME_Translations_MAPPING,
} from '@shared/types';
import { MODAL_TEMPLATES } from '@app/text-content';
import { BTN_CANCEL, BTN_YES } from '@app/text-content/button-templates';
import {
  getFormControl,
  setFormFieldValue,
} from '@app/components/form/services/schema-form/schema-form-functions';
import { UiActions } from '@store/actions';
import { STATE_KEYS, TransferService } from '@shared/services';
import { BaseEditorContainerComponent } from '@app/containers/base';
import { lazyInject } from '@containers/base/decorators/lazy-inject.decorator';
import { TransferFormService } from '@shared/services/transfer/transfer.form.service';
import { TRANSFER_OPERATION_STATUS_MAPPING } from '@shared/constants';
import { getSingularEntityName } from '@shared/configs';
import { BTN_PRIMARY_CLASS, KEY_CODES } from '@config/global-constants';
import {
  flatTreeDataByHasChildren,
  getCombinedToolbarButtonHidden,
  getFormBtnHidden,
  getObservableValue,
  judgeScanned,
} from '@app/shared/utils';
import { TransferPdfService } from '@app/shared/services/transfer/transfer.pdf.service';
import { PdfGenerateServiceV2 } from '@app/shared/common/make-pdf/v2/pdf.generate.service';
import { IPdfDefinitions } from '@shared/common/make-pdf/v2';
import { getAutoCompleteTransferNextStatus } from '@app/shared/services/transfer/transfer.util';
import { TransferCsvService } from '@app/shared/services/transfer/transfer.csv.service';

import {
  ITransferFormFieldPath,
  NotificationType,
  TRANSFER_TYPE,
} from '../../../../shared/types';
import { getFormFieldValue } from '../../../../components/form/services/schema-form/schema-form-functions';
import {
  TOASTER_ERROR_MESSAGE,
  TOASTER_SUBMITTED_SUCCESSFULLY_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
  TOASTER_TRANSACTION_FAILED_MESSAGE,
} from '../../../../shared/constants';
import { I18n } from '@app/i18n/strings-keys';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-transfer-edit-container',
  templateUrl: '../../../base/editor/xux-edit.base.container.html',
  styleUrls: [
    '../../../base/editor/xux-edit.base.container.scss',
    './edit.transfer.form.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class TransferEditContainerComponent
  extends BaseEditorContainerComponent
  implements OnInit, OnDestroy
{
  @select(STATE_KEYS.UI_EDITOR_OPTIONS_STATE)
  public routeState$: Observable<IPageJumpRouteState>;

  @lazyInject(TransferFormService)
  public entityFormService: TransferFormService;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(TransferPdfService) public transferPdfService: TransferPdfService;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;
  @lazyInject(TransferCsvService) public transferCsvService: TransferCsvService;

  public xuxTheme: boolean = true;
  public formRules: IFormRules;
  public entityIdPath: string = 'transaction_id';
  public title: string = '';
  public showExportButton: boolean = true;
  public showToggleEmptyPrintButtons: boolean = false;

  public canExportPDF: boolean = true;
  public canPrint: boolean = true;

  public formFieldPath: ITransferFormFieldPath =
    this.entityFormService.formFieldPath;

  public isUnacceptable$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public isUnrejectable$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  public showToggleExportButtons: boolean = true;

  // define the container whether need organization the  get_updated_items to api parameters
  public getUpdatedItemsDefaultValue: boolean = null;

  private isHiddenVoidBtn: boolean = true;

  public get showQohFrom(): boolean {
    const siteId: string = this.getFormFieldValue(this.formFieldPath.siteId);

    return this.transferService.getDisplayQohFrom(
      siteId,
      getObservableValue(this.entityFormService?.siteIdDataSource?.items),
    );
  }

  @HostListener('window:keydown', ['$event'])
  public onClick($event: KeyboardEvent): void {
    this.entityFormService.scanningService.keyTimes.push(Date.now());
    if (
      includes([KEY_CODES.TAB, KEY_CODES.ENTER], $event.key) &&
      judgeScanned(this.entityFormService.scanningService.keyTimes)
    ) {
      $event.preventDefault();
    }
  }

  protected createWithDataCarryFields: string[] = [
    this.formFieldPath.transferType,
    this.formFieldPath.eventId,
    this.formFieldPath.siteId,
    this.formFieldPath.siteName,
    this.formFieldPath.siteIdTo,
    this.formFieldPath.siteNameTo,
    this.formFieldPath.transactionDate,
    this.formFieldPath.virtualTransactionDate,
  ];

  public ngOnInit(): void {
    this.formRules = this.entityFormService.createFormRules(this);
    super.ngOnInit();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.isUnacceptable$.next(true);
    this.isUnrejectable$.next(true);
  }

  public async onFormReady(form: FormGroup): Promise<void> {
    super.onFormReady(form, this.entityIdPath);
    this.subscriptions.push(this.initTopButtonBuildSubscription());

    this.initIsUnacceptableSubscription();
    this.initIsHiddenVoidBtnSubscription();
    this.showToggleEmptyPrintButtons =
      this.entityFormService.canPrintEmptyPdf();
  }

  public getIsHiddenVoidButtonObs(): Observable<boolean> {
    const status = this.getFormFieldValue(this.formFieldPath.status);
    const documentId = get(this.document, '_id');

    return observableOf(isEqual(TRANSFER_STATUS_VALUE.Completed, status)).pipe(
      rx.switchMap((isCompleted) => {
        return !isCompleted
          ? observableOf(true)
          : this.transferService
              .validateChange({
                _id: [documentId],
                _change: {
                  status: TRANSFER_STATUS_VALUE.Void,
                },
              })
              .pipe(
                rx.filter(negate(isNil)),
                rx.map((validationResult: EntityValidateResponse) => {
                  const curValidateRes = find(validationResult, {
                    _id: documentId,
                  });

                  return !curValidateRes?.allowed;
                }),
              );
      }),
      rx.startWith(true),
    );
  }

  protected getSidebarOperationStatusMapping(): Record<string, string> {
    return TRANSFER_OPERATION_STATUS_MAPPING;
  }

  public checkAndSendIsUnacceptable(): void {
    if (!this.canManage) {
      this.isUnacceptable$.next(true);
      this.isUnrejectable$.next(true);

      return;
    }

    const flattenInventoryItemVenues =
      flatTreeDataByHasChildren<IGridFlatInventoryItemVenue>(
        this.getFormFieldValue(this.formFieldPath.inventoryItemVenues),
      );

    // if inventory item is empty or all quantity not be filled or all quantity not greater than 0
    const isUnacceptable =
      isEmpty(flattenInventoryItemVenues) ||
      !some(
        flattenInventoryItemVenues,
        (inventoryItemVenue: IGridFlatInventoryItemVenue) =>
          gt(inventoryItemVenue[this.formFieldPath.count], 0) ||
          lt(inventoryItemVenue[this.formFieldPath.count], 0) ||
          gt(inventoryItemVenue[this.formFieldPath.initialCount], 0) ||
          lt(inventoryItemVenue[this.formFieldPath.initialCount], 0),
      );

    this.isUnacceptable$.next(isUnacceptable);
    this.isUnrejectable$.next(
      this.entityFormService.isPreEventTypeTransfer() ? false : isUnacceptable,
    );
  }

  public initIsHiddenVoidBtnSubscription(): void {
    this.subscriptions.push(
      this.getIsHiddenVoidButtonObs().subscribe((isHiddenVoidBtn) => {
        this.isHiddenVoidBtn = isHiddenVoidBtn;
      }),
    );
  }

  public initIsUnacceptableSubscription(): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.inventoryItemVenues).subscribe(
        () => this.checkAndSendIsUnacceptable(),
      ),
    );
  }

  public async saveButtonClickHandler(
    documentItem: IBaseEntityType = this.document,
    customRedirect?: CustomRedirect,
    needCachePopulateEvent: boolean = true,
  ): Promise<void> {
    if (!this.form?.dirty) return;

    if (
      !isEmpty(get(this.document, '_id')) &&
      (await this.checkStatusBeforeSave(TRANSFER_SAVE_OPERATION.SAVE))
    ) {
      return;
    }

    // pass transfer type to api mapper, need to remove it if not  before send to api
    this.markFieldsAsDirty(['status', this.formFieldPath.transferType]);
    await this.doAcknowledge();
    const saveDocument = await this.submitBtnClick(documentItem);
    // The document is `undefined` in case of Error, that is, we need to stay on the Add/Edit page
    if (!saveDocument || (!saveDocument._id && this.isNewMode())) return;

    if (needCachePopulateEvent) {
      this.entityFormService.saveTransferAutoPopulateEvent(saveDocument);
    }

    this.uiActions.showToasterMessage({
      type: NotificationType.SUCCESS,
      title: TOASTER_SUCCESS_MESSAGE.title,
      body: this.i18nService.translate(I18n.InfoMessages.Didsuccessfully, {
        did: this.i18nService.translate(STATUS_ACTION_MAPPING['save']),
      }),
    });
    const _id = this.getIdForRedirect(saveDocument);

    const saveDocWithRedirectId = { ...saveDocument, _id };

    this.redirectAfterSave(this.entityType, saveDocWithRedirectId, () => {
      const routeParams = `${_id}`;

      const redirectFunc = isFunction(customRedirect)
        ? customRedirect
        : this.getRedirectExecutor(routeParams, undefined, {
            get_updated_items: true,
          }).bind(this);

      redirectFunc(saveDocWithRedirectId);
    });
  }

  public async saveAndSubmitButtonClickHandler(
    documentItem: ITransfer = this.document,
    customRedirect?: CustomRedirect,
    needCachePopulateEvent: boolean = true,
  ): Promise<void> {
    let saveDocument = documentItem;
    const modalOptions: IModalDialogOptions = {
      title: MODAL_TEMPLATES.MODAL_TEMPLATE_CONFIRM.title,
      text: this.i18nService.translate(I18n.ConfirmMessages.SubmitEntity, {
        entityType: this.i18nService.translate(
          getSingularEntityName(this.entityType),
        ),
      }),
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        BTN_CANCEL,
      ],
    };
    const result = await this.uiActions.promptFormModal(modalOptions);
    const transferType: TRANSFER_TYPE = this.getFormFieldValue(
      this.formFieldPath.transferType,
    );
    const transferStatus: TRANSFER_STATUS_VALUE = this.getFormFieldValue(
      this.formFieldPath.status,
    );
    if (!result.confirmed) {
      return;
    }

    if (
      !isEmpty(get(this.document, '_id')) &&
      (await this.checkStatusBeforeSave(lowerCase(COMMON_ACTION_NAMES.Submit)))
    ) {
      return;
    }

    await this.doAcknowledge();

    if (this.form.dirty) {
      saveDocument = await this.submitBtnClick(documentItem);
      // The document is `undefined` in case of Error, that is, we need to stay on the Add/Edit page
      if (!saveDocument || (!saveDocument._id && this.isNewMode())) return;
    }

    if (needCachePopulateEvent) {
      this.entityFormService.saveTransferAutoPopulateEvent(saveDocument);
    }

    const updatedTransferStatus: TRANSFER_STATUS_VALUE =
      getAutoCompleteTransferNextStatus(
        transferType,
        transferStatus,
        this.stateService,
      );
    try {
      await this.transferService
        .updateEntity(
          {
            [this.formFieldPath.status]: updatedTransferStatus,
          },
          { endpoint: saveDocument._id },
        )
        .toPromise();
    } catch (e) {
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: TOASTER_TRANSACTION_FAILED_MESSAGE,
      });

      return;
    }
    this.uiActions.showToasterMessage({
      type: NotificationType.SUCCESS,
      title: TOASTER_SUCCESS_MESSAGE.title,
      body: TOASTER_SUBMITTED_SUCCESSFULLY_MESSAGE,
    });
    const _id = this.getIdForRedirect(saveDocument);

    const savedDocWithRedirectId = { ...saveDocument, _id };

    this.redirectAfterSave(this.entityType, savedDocWithRedirectId, () => {
      const redirectFunc = isFunction(customRedirect)
        ? customRedirect
        : this.getRedirectExecutor(_id).bind(this);

      redirectFunc(savedDocWithRedirectId);
    });
  }

  public async cancelTransferButtonClickHandler(
    documentItem: ITransfer = this.document,
  ): Promise<void> {
    const message = this.i18nService.translate(
      I18n.ConfirmMessages.CancelEntity,
      {
        entityType: this.i18nService.translate(
          getSingularEntityName(this.entityType),
        ),
      },
    );

    const modalOptions: IModalDialogOptions = {
      title: I18n.Common.Confirm,
      text: message,
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        BTN_CANCEL,
      ],
    };
    const result = await this.uiActions.promptFormModal(modalOptions);
    if (!result.confirmed) {
      return;
    }

    if (await this.checkStatusBeforeSave(COMMON_ACTION_TYPES.Cancel)) {
      return;
    }

    await this.doAcknowledge();

    try {
      this.uiActions.enableLoader();
      await this.transferService
        .updateEntity(
          { [this.formFieldPath.status]: TRANSFER_STATUS_VALUE.Canceled },
          { endpoint: documentItem._id },
        )
        .toPromise();
    } catch (e) {
      setFormFieldValue(
        this.form,
        this.formFieldPath.status,
        get(this.document, this.formFieldPath.status, ''),
        false,
      );
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: TOASTER_TRANSACTION_FAILED_MESSAGE,
      });

      return;
    } finally {
      this.uiActions.disableLoader();
    }
    this.uiActions.showToasterMessage({
      type: NotificationType.SUCCESS,
      title: TOASTER_SUCCESS_MESSAGE.title,
      body: this.i18nService.translate(I18n.InfoMessages.Didsuccessfully, {
        did: this.i18nService.translate(
          STATUS_ACTION_MAPPING[TRANSFER_STATUS_VALUE.Canceled],
        ),
      }),
    });
    this.form.markAsPristine();
    this.redirectAfterSave(
      this.entityType,
      { ...documentItem },
      this.getRedirectExecutor(documentItem._id).bind(this),
    );
  }

  public async voidTransferButtonClickHandler(
    documentItem: ITransfer = this.document,
  ): Promise<void> {
    const modalOptions: IModalDialogOptions = {
      title: I18n.Common.Confirm,
      text: this.i18nService.instant(I18n.InfoMessages.VoidAdverselyImpactQOH, {
        target: this.i18nService.translate(
          I18n.Forms.TransferForm.ThisTransfer,
        ),
      }),
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        BTN_CANCEL,
      ],
    };
    const result = await this.uiActions.promptFormModal(modalOptions);
    if (!result.confirmed) {
      return;
    }

    if (await this.checkStatusBeforeSave(TRANSFER_STATUS_VALUE.Void)) {
      return;
    }

    try {
      this.uiActions.enableLoader();
      await this.transferService
        .updateEntity(
          { [this.formFieldPath.status]: TRANSFER_STATUS_VALUE.Void },
          { endpoint: documentItem._id },
        )
        .toPromise();
    } catch (error) {
      setFormFieldValue(
        this.form,
        this.formFieldPath.status,
        get(this.document, this.formFieldPath.status, ''),
        false,
      );
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: error?.meta?.message || TOASTER_TRANSACTION_FAILED_MESSAGE,
      });

      return;
    } finally {
      this.uiActions.disableLoader();
    }
    this.uiActions.showToasterMessage({
      type: NotificationType.SUCCESS,
      title: TOASTER_SUCCESS_MESSAGE.title,
      body: this.i18nService.translate(I18n.InfoMessages.Didsuccessfully, {
        did: this.i18nService.translate(
          STATUS_ACTION_MAPPING[TRANSFER_STATUS_VALUE.Void],
        ),
      }),
    });
    this.form.markAsPristine();
    this.redirectAfterSave(
      this.entityType,
      { ...documentItem },
      this.getRedirectExecutor(documentItem._id).bind(this),
    );
  }

  /**
   * build page top buttons with form status
   *
   * @returns form status change subscription
   */
  public initTopButtonBuildSubscription(): Subscription {
    const acceptButtonClickHandler = this.getStatusOperationClickHandler(
      TRANSFER_STATUS_VALUE.Completed,
      {
        text: this.i18nService.translate(I18n.ConfirmMessages.AcceptEntity, {
          entityType: this.i18nService.translate(
            getSingularEntityName(ENTITY_TYPES.TRANSFER),
          ),
        }),
      },
    );
    const rejectAllButtonClickHandler = this.getStatusOperationClickHandler(
      TRANSFER_STATUS_VALUE.Rejected,
      {
        text: this.i18nService.translate(
          I18n.ConfirmMessages.RejectEntireEntity,
          {
            entityType: this.i18nService.translate(
              getSingularEntityName(ENTITY_TYPES.TRANSFER),
            ),
          },
        ),
      },
    );

    return observableCombineLatest([
      this.getFieldChangeFilterEmpty(this.formFieldPath.status),
      this.entityFormService.isInventoryItemVenuesGridBusy$,
    ]).subscribe(
      ([status, isInventoryItemVenuesGridBusy]: [
        TRANSFER_STATUS_VALUE,
        boolean,
      ]) => {
        const transferType = this.getFormFieldValue(
          this.formFieldPath.transferType,
        );
        const isPreEvent = isEqual(
          TRANSFER_TYPE.PRE_EVENT_TRANSFER,
          transferType,
        );
        const isOpenStatus = [
          TRANSFER_STATUS_VALUE.New,
          TRANSFER_STATUS_VALUE.Open,
        ].includes(status);
        const isPendingStatus = isEqual(TRANSFER_STATUS_VALUE.Pending, status);
        const documentId = get(this.document, '_id');

        const enableCreateNewBtnPages = ['transfer'];
        const disableCreateNewBtnPages = ['transfer/addHistorical'];

        const routeUrl = head(split(this.router.url, '?'));

        const isEnableCreateNewBtn =
          some(enableCreateNewBtnPages, (pagePath) =>
            startsWith(routeUrl, `/${pagePath}`),
          ) &&
          every(
            disableCreateNewBtnPages,
            (disablePagePath) => !isEqual(`/${disablePagePath}`, routeUrl),
          );

        this.pageTopButtons$.next([
          {
            caption: I18n.Common.Accept,
            class: 'btn btn-primary mr-10',
            click: acceptButtonClickHandler.bind(this),
            disabledObs: this.isUnacceptable$,
            hidden: isPreEvent || !isPendingStatus,
          },
          {
            caption: I18n.Common.Reject,
            class: 'btn btn-danger mr-10',
            click: rejectAllButtonClickHandler.bind(this),
            disabledObs: this.isUnrejectable$,
            hidden: (): boolean => !isPendingStatus || !documentId,
          },
          {
            caption: I18n.Status.Void,
            class: 'btn btn-default mr-10',
            click: this.voidTransferButtonClickHandler.bind(this),
            hidden: (): boolean =>
              !isEqual(
                TRANSFER_STATUS_VALUE.Completed,
                getFormFieldValue(this.form, this.formFieldPath.status),
              ) ||
              !(this.canManage && this.transferService.hasVoidPermission()) ||
              this.isHiddenVoidBtn,
          },
          this.topSaveButtonDefinition,
        ]);

        this.pageTopDropdownButtons$.next([
          {
            caption: I18n.Common.Submit,
            value: 'edit-xux-btn-save-and-submit',
            click: this.saveAndSubmitButtonClickHandler.bind(this),
            hidden: (): boolean =>
              !this.form?.valid ||
              !this.canEdit ||
              !this.canManage ||
              !isOpenStatus ||
              isInventoryItemVenuesGridBusy ||
              this.entityFormService.isFinalStatusFun(),
          },
          {
            ...this.saveAndNewButtonDefinition,
            hidden: (): boolean => {
              return (
                isEqual(
                  this.entityConfig.uiRoute,
                  ENTITY_TYPES.TRANSFER_REQUEST,
                ) ||
                !isEnableCreateNewBtn ||
                !(isOpenStatus || isPendingStatus) ||
                isInventoryItemVenuesGridBusy ||
                getFormBtnHidden(this.saveAndNewButtonDefinition.hidden)
              );
            },
          },
          {
            ...this.submitAndNewButtonDefinition,
            hidden: (): boolean => {
              return (
                isEqual(
                  this.entityConfig.uiRoute,
                  ENTITY_TYPES.TRANSFER_REQUEST,
                ) ||
                !isEnableCreateNewBtn ||
                !isOpenStatus ||
                isInventoryItemVenuesGridBusy ||
                this.entityFormService.isFinalStatusFun() ||
                getFormBtnHidden(this.submitAndNewButtonDefinition.hidden)
              );
            },
          },
          {
            ...this.duplicateButtonDefinition,
            hidden: getCombinedToolbarButtonHidden(
              this.duplicateButtonDefinition.hidden,
              () =>
                !includes(
                  [
                    TRANSFER_STATUS_VALUE.Open,
                    TRANSFER_STATUS_VALUE.Pending,
                    TRANSFER_STATUS_VALUE.Completed,
                  ],
                  status,
                ),
            ),
          },
          {
            ...this.discardButtonDefinition,
            hidden: getCombinedToolbarButtonHidden(
              this.discardButtonDefinition.hidden,
              () => this.entityFormService.isFinalStatusFun(),
            ),
          },
          {
            caption: I18n.Forms.TransferForm.CancelTransfer,
            value: 'edit-xux-btn-cancel-order',
            click: this.cancelTransferButtonClickHandler.bind(this),
            hidden: (): boolean =>
              !this.canManage ||
              !isOpenStatus ||
              !documentId ||
              this.entityFormService.isFinalStatusFun(),
          },
        ]);
      },
    );
  }

  public async saveAndNewButtonClickHandler(
    documentItem: ITransfer = this.document,
  ): Promise<void> {
    await this.saveButtonClickHandler(
      documentItem,
      this.getCreateWithDataRedirect().bind(this),
      false,
    );
  }

  public async submitAndNewButtonClickHandler(
    documentItem: ITransfer = this.document,
  ): Promise<void> {
    await this.saveAndSubmitButtonClickHandler(
      documentItem,
      this.getCreateWithDataRedirect().bind(this),
      false,
    );
  }

  public getStatusOperationClickHandler(
    targetStatus: TRANSFER_STATUS_VALUE,
    additionModalOptions: IModalDialogOptions,
  ): (documentItem: ITransfer) => Promise<void> {
    return async (documentItem: ITransfer): Promise<void> => {
      const modalOptions: IModalDialogOptions = merge(
        {
          title: MODAL_TEMPLATES.MODAL_TEMPLATE_CONFIRM.title,
          buttons: [
            {
              ...BTN_YES,
              class: BTN_PRIMARY_CLASS,
            },
            BTN_CANCEL,
          ],
        },
        additionModalOptions,
      );

      const result = await this.uiActions.promptFormModal(modalOptions);
      if (!result.confirmed) return;
      const statusMap = {
        [TRANSFER_STATUS_VALUE.Completed]: COMMON_ACTION_TYPES.Accept,
        [TRANSFER_STATUS_VALUE.Rejected]: COMMON_ACTION_TYPES_REJECT,
      };
      if (await this.checkStatusBeforeSave(statusMap[targetStatus])) {
        return;
      }

      await this.doAcknowledge();
      setFormFieldValue(this.form, this.formFieldPath.status, targetStatus);

      const skipValidate =
        this.entityFormService.isPreEventTypeTransfer() &&
        targetStatus === TRANSFER_STATUS_VALUE.Rejected;

      try {
        if (skipValidate) {
          this.markFieldsAsDirty([this.formFieldPath.transferType]);
        }

        const saveDocument = await this.submitBtnClick(documentItem, {
          skipValidate,
        });
        if (saveDocument) {
          this.uiActions.showToasterMessage({
            type: NotificationType.SUCCESS,
            title: TOASTER_SUCCESS_MESSAGE.title,
            body: this.i18nService.translate(
              I18n.InfoMessages.Didsuccessfully,
              {
                did: this.i18nService.translate(
                  STATUS_ACTION_MAPPING[targetStatus],
                ),
              },
            ),
          });

          this.redirectAfterSave(
            this.entityType,
            { ...documentItem },
            this.getRedirectExecutor(documentItem._id).bind(this),
          );
        } else {
          setFormFieldValue(
            this.form,
            this.formFieldPath.status,
            get(this.document, this.formFieldPath.status, ''),
            false,
          );
        }
      } finally {
        if (skipValidate) {
          getFormControl(
            this.form,
            this.formFieldPath.transferType,
          ).markAsPristine();
        }
      }
    };
  }

  public async checkStatusBeforeSave(action: string): Promise<boolean> {
    const currentDocument =
      await this.transferService.getDocumentByIdWithSkipUpdateDocument(
        this.document?._id,
        {
          noLoader: true,
          query: {
            get_updated_items: false,
          },
          suppressErrors: true,
        },
      );

    const actualStatus = get(currentDocument, this.formFieldPath.status);
    if (
      !isEqual(actualStatus, this.getFormFieldValue(this.formFieldPath.status))
    ) {
      const operateSuccess: boolean =
        (isEqual(actualStatus, TRANSFER_STATUS_VALUE.Canceled) &&
          isEqual(action, COMMON_ACTION_TYPES.Cancel)) ||
        (isEqual(actualStatus, TRANSFER_STATUS_VALUE.Void) &&
          isEqual(action, TRANSFER_STATUS_VALUE.Void));

      this.uiActions.showToasterMessage({
        type: operateSuccess
          ? NotificationType.SUCCESS
          : NotificationType.ERROR,
        title: operateSuccess
          ? TOASTER_SUCCESS_MESSAGE.title
          : TOASTER_ERROR_MESSAGE.title,
        body: operateSuccess
          ? this.i18nService.translate(I18n.InfoMessages.Didsuccessfully, {
              did: this.i18nService.translate(
                STATUS_ACTION_MAPPING[actualStatus],
              ),
            })
          : this.i18nService.translate(I18n.ErrorMessages.FailedToDoAndWhy, {
              wantTo: this.i18nService.translate(
                ACTION_NAME_Translations_MAPPING[action],
              ),
              did: this.i18nService.translate(
                TRANSFER_STATUS_NAME_OPTIONS[upperFirst(actualStatus)],
              ),
            }),
      });
      this.form.markAsPristine();

      this.redirectAfterSave(
        this.entityType,
        { ...this.document },
        this.getRedirectExecutor(this.document._id).bind(this),
      );

      return true;
    }

    return false;
  }

  public disabledPrintOrExportPdf(): boolean {
    return (
      this.isNewMode() ||
      this.entityFormService.isQohDataFetching ||
      this.entityFormService.isPendingQuantityFetching
    );
  }

  public disabledPrintEmptyColumnBtn(): boolean {
    return this.disabledPrintOrExportPdf();
  }

  public isNewStatusTransfer(): boolean {
    const status = this.getFormFieldValue(this.formFieldPath.status);

    return isEqual(status, TRANSFER_STATUS_VALUE.New);
  }

  public exportCsvClickHandler(): void {
    this.transferCsvService.handleCsvClick(this);
  }

  protected getEntityRequiredFieldsForUpdate(): string[] {
    return [
      '_id',
      'event_id',
      'site_id',
      'site_id_to',
      'status',
      'inventory_items',
      'transaction_date',
      'transaction_id',
      'transfer_type',
      'type',
    ];
  }

  public async printEmptyColumnPdf(): Promise<void> {
    const isOpenTransfer = isEqual(
      this.document.status,
      TRANSFER_STATUS_VALUE.Open,
    );
    let allInventoryItems = this.entityFormService.allInventoryItems;
    if (this.entityFormService.isListView()) {
      allInventoryItems = sortBy(allInventoryItems, (item) =>
        toLower(item?.name),
      );
    }
    const pdfConfig = await this.transferService.getWrittenSheetPdfConfig(
      (definitions: IPdfDefinitions) =>
        this.transferService.generateWrittenSheetPdfData(
          merge(
            {
              displayQoh: this.showQohFrom,
              internal_number: this.document?.internal_number,
            },
            {
              ...this.form.value,
              inventory_items: allInventoryItems,
            },
          ),
          true,
          this,
          definitions?.options,
        ),
      !isOpenTransfer,
      null,
      {
        showQoh: this.showQohFrom,
        isTransferGroup: false,
      },
    );
    await this.pdfGenerateServiceV2.printPdfAsync(pdfConfig);
  }

  public async printPdfClickHandler(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    params: Record<string, string>,
  ): Promise<void> {
    if (!this.canPrint) {
      return;
    }

    await this.transferPdfService.handlePdfClick(this, 'print');
  }

  public async exportPdfClickHandler(): Promise<void> {
    if (!this.canExportPDF) {
      return;
    }

    await this.transferPdfService.handlePdfClick(this, 'export');
  }

  protected getFieldsToSetDisable(): string[] {
    return has(this.document, 'transfer_group_entity_id')
      ? [
          this.formFieldPath.type,
          this.formFieldPath.transferType,
          this.formFieldPath.eventId,
          this.formFieldPath.siteId,
          this.formFieldPath.siteIdTo,
          this.formFieldPath.storeNumber,
          this.formFieldPath.storeNumberTo,
          this.formFieldPath.transactionDate,
          this.formFieldPath.status,
        ]
      : [];
  }
}
